import { useEffect, useMemo, useState } from 'react';

import { getGeneratingContentLoadingMsgsTimeInterval } from '../services/ispmUtil';

// Define the type for the data object: keys are strings, and values are arrays of strings.
type DataObject = Record<string, string[]>;

const useGeneratingContentLoadingMessages = (
  sidePanelDataLoaderTexts: DataObject
) => {
  const [loaderTitleIndex, setLoaderTitleIndex] = useState<number>(0); // Track the current parent index
  const [loaderDescriptionIndex, setLoaderDescriptionIndex] =
    useState<number>(0); // Track the current child index

  const keys: string[] = useMemo(
    () => Object.keys(sidePanelDataLoaderTexts),
    [sidePanelDataLoaderTexts]
  ); // Extract keys from data

  const loaderTitleText: string = useMemo(
    () => keys[loaderTitleIndex],
    [keys, loaderTitleIndex]
  ); // Get current parent text

  const loaderDescriptionText: string = useMemo(
    () => sidePanelDataLoaderTexts[loaderTitleText][loaderDescriptionIndex], // Get current child text
    [sidePanelDataLoaderTexts, loaderTitleText, loaderDescriptionIndex]
  );

  useEffect(() => {
    let interval: string | number | NodeJS.Timeout | undefined;

    if (sidePanelDataLoaderTexts && keys.length > 0) {
      interval = setTimeout(() => {
        const currentChildArray = sidePanelDataLoaderTexts[loaderTitleText]; // Get the current child array based on parent text

        if (
          currentChildArray &&
          currentChildArray.length > 0 &&
          loaderDescriptionIndex < currentChildArray.length - 1
        ) {
          // Move to the next child
          setLoaderDescriptionIndex((prev) => prev + 1);
        } else if (loaderTitleIndex < keys.length - 1) {
          // Move to the next parent and reset child index
          setLoaderDescriptionIndex(0);
          setLoaderTitleIndex((prev) => prev + 1);
        }

        clearTimeout(interval);
      }, getGeneratingContentLoadingMsgsTimeInterval()); // Update every 5 seconds
    }

    return () => clearTimeout(interval); // Cleanup interval on unmount
  }, [
    loaderDescriptionIndex,
    loaderTitleIndex,
    loaderTitleText,
    sidePanelDataLoaderTexts,
    keys,
  ]);

  return { loaderTitleText, loaderDescriptionText };
};

export default useGeneratingContentLoadingMessages;
